import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { allContent, siteInfo } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

type CategoryType = 'movie' | 'tv' | 'variety';

const categoryMeta: Record<CategoryType, { label: string; icon: string; desc: string }> = {
  movie: { label: '电影', icon: 'ri-movie-2-line', desc: '最新院线大片、经典佳片、高分电影推荐' },
  tv: { label: '电视剧', icon: 'ri-tv-line', desc: '热播剧集、高分神剧、全网热剧一网打尽' },
  variety: { label: '综艺', icon: 'ri-emotion-laugh-line', desc: '热门综艺、搞笑节目、音乐竞技精彩纷呈' },
};

const sortOptions = [
  { value: 'rating', label: '评分最高' },
  { value: 'newest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const categoryKey = (type || 'movie') as CategoryType;
  const meta = categoryMeta[categoryKey] || categoryMeta.movie;

  const [activeTab, setActiveTab] = useState<CategoryType>(categoryKey);
  const [sortBy, setSortBy] = useState('rating');
  const [selectedTag, setSelectedTag] = useState<string>('全部');

  useEffect(() => {
    setActiveTab(categoryKey);
    setSelectedTag('全部');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryKey]);

  const categoryItems = useMemo(() => {
    return allContent.filter((item) => item.category === activeTab);
  }, [activeTab]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    categoryItems.forEach((item) => item.tags.forEach((tag) => tags.add(tag)));
    return ['全部', ...Array.from(tags)];
  }, [categoryItems]);

  const filteredItems = useMemo(() => {
    let items = selectedTag === '全部'
      ? categoryItems
      : categoryItems.filter((item) => item.tags.includes(selectedTag));

    if (sortBy === 'rating') {
      items = [...items].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'newest') {
      items = [...items].sort((a, b) => b.updateInfo.localeCompare(a.updateInfo));
    }
    return items;
  }, [categoryItems, selectedTag, sortBy]);

  const tabs: { key: CategoryType; label: string }[] = [
    { key: 'movie', label: '电影' },
    { key: 'tv', label: '电视剧' },
    { key: 'variety', label: '综艺' },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${meta.label} - ${siteInfo.name}`,
    description: `${meta.desc}，尽在${siteInfo.name}。`,
    url: `${siteInfo.url}/category/${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: meta.label },
                ]}
              />
            </div>

            {/* Category header */}
            <div className="py-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
                  <i className={`${meta.icon} text-background-50 text-lg`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    {meta.label}
                  </h1>
                  <p className="text-foreground-500 text-sm mt-0.5">{meta.desc}</p>
                </div>
              </div>
            </div>

            {/* Tab switcher */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex bg-background-100 rounded-full p-1">
                {tabs.map((tab) => (
                  <Link
                    key={tab.key}
                    to={`/category/${tab.key}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === tab.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {tab.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Filter bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              {/* Tag filters */}
              <div className="flex items-center gap-2 flex-wrap">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      selectedTag === tag
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-foreground-500 text-xs">排序：</span>
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-accent-500 text-background-950'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5 pb-12">
              {filteredItems.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-search-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-lg">暂无相关内容</p>
                <p className="text-foreground-400 text-sm mt-1">请尝试切换筛选条件</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}